import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Search, UserPlus } from "lucide-react";
import { api, formatApiError } from "../../api";
import { LEADS } from "../../constants/testIds";
import { LEAD_TABS, CONTACTED_SUB_STATUSES, isToday, leadTabKey } from "../../lib/leadTabs";
import { fadeUp } from "../../lib/motion";
import { useSortPersistence } from "../../hooks/useSortPersistence";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { GymLoader } from "../common/GymLoader";
import { ErrorState } from "../common/ErrorState";
import { SortControl } from "../common/SortControl";
import { toast } from "../ui/sonner";
import { LeadTable } from "./LeadTable";
import { LeadDetailDrawer } from "./LeadDetailDrawer";
import { ConvertLeadWizard } from "./ConvertLeadWizard";
import { LeadCaptureForm } from "./LeadCaptureForm";

const LEAD_SORT_FIELDS = [{ value: "updated_at", label: "Newest first", type: "date" }, { value: "name", label: "Name (A–Z)", type: "alpha" }];

// UX-CRM-03: the Lead module's main screen — 5 horizontal tabs (New Lead/Lead Pipeline/Contacted/
// Converted-Member/Lost), each an Excel-style `LeadTable`, replacing the earlier drag-free Kanban
// board per explicit user request (see `lib/leadTabs.js` for the full tab-derivation rationale).
// Shared verbatim by the Desk (`/desk/leads`, Manager+Front Desk, `branchId` fixed) and Owner
// Cockpit (`/app/leads`, Super Admin, `branchId` can be "all") pages — same as the old board was.
export function LeadTabsBoard({ branchId, branches, memberDetailPath }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("new_lead");
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [subStatusFilter, setSubStatusFilter] = useState("all");
  const [detailLead, setDetailLead] = useState(null);
  const [convertLead, setConvertLead] = useState(null);
  const [captureOpen, setCaptureOpen] = useState(false);

  const leadsQ = useQuery({
    queryKey: ["leads", branchId],
    queryFn: async () => (await api.get("/leads", { params: { branch_id: branchId === "all" ? undefined : branchId, limit: 500 } })).data.leads,
  });
  const convertedQ = useQuery({
    queryKey: ["leads-converted", branchId],
    queryFn: async () => (await api.get("/leads", { params: { branch_id: branchId === "all" ? undefined : branchId, stage: "converted", limit: 200 } })).data.leads,
    enabled: activeTab === "converted",
  });
  const plansQ = useQuery({ queryKey: ["plans-for-leads"], queryFn: async () => (await api.get("/plans")).data.plans });
  const planById = useMemo(() => Object.fromEntries((plansQ.data || []).map((p) => [p.id, p])), [plansQ.data]);
  const branchNameById = useMemo(() => Object.fromEntries((branches || []).map((b) => [b.id, b.name])), [branches]);

  const allLeads = useMemo(() => leadsQ.data || [], [leadsQ.data]);
  const bucketed = useMemo(() => {
    const groups = { new_lead: [], pipeline: [], contacted: [], lost: [] };
    for (const lead of allLeads) {
      const key = leadTabKey(lead);
      if (key && key !== "converted" && groups[key]) groups[key].push(lead);
    }
    return groups;
  }, [allLeads]);
  const convertedToday = useMemo(() => (convertedQ.data || []).filter((l) => isToday(l.updated_at)), [convertedQ.data]);
  const overdueContactedCount = useMemo(
    () => (bucketed.contacted || []).filter((l) => l.follow_up_at && new Date(l.follow_up_at).getTime() < Date.now()).length,
    [bucketed.contacted],
  );

  const campaignsQ = useQuery({
    queryKey: ["lead-source-campaigns"],
    queryFn: async () => (await api.get("/marketing/campaigns")).data.campaigns,
    retry: false,
  });

  const sourceOptions = useMemo(() => {
    const set = new Set(allLeads.map((l) => l.source).filter(Boolean));
    return Array.from(set);
  }, [allLeads]);

  // A lead credited to an ad carries "campaign:<id>" as its source. Show the campaign's name
  // instead of the raw id, falling back to a readable label if that campaign is gone.
  const campaignNames = useMemo(() => {
    const map = {};
    for (const c of campaignsQ.data || []) map[c.lead_source_tag] = c.name;
    return map;
  }, [campaignsQ.data]);
  const sourceLabel = (s) =>
    s.startsWith("campaign:") ? `Ad: ${campaignNames[s] || "campaign"}` : s.replace(/_/g, " ");

  const tabLeads = activeTab === "converted" ? convertedToday : (bucketed[activeTab] || []);
  const { sortBy, sortDir, setSort } = useSortPersistence("leads-list", "updated_at", "desc");
  const filteredLeads = tabLeads.filter((l) => {
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!l.contact.name.toLowerCase().includes(q) && !l.contact.phone.includes(q)) return false;
    }
    if (sourceFilter !== "all" && l.source !== sourceFilter) return false;
    if (activeTab === "contacted" && subStatusFilter !== "all" && l.stage !== subStatusFilter) return false;
    return true;
  }).sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortBy === "name") return a.contact.name.localeCompare(b.contact.name) * (sortDir === "asc" ? 1 : -1);
    return (new Date(a.updated_at) - new Date(b.updated_at)) * dir;
  });

  const invalidate = () => { queryClient.invalidateQueries({ queryKey: ["leads"] }); queryClient.invalidateQueries({ queryKey: ["leads-converted"] }); };

  const stageMutation = useMutation({
    mutationFn: ({ id, stage }) => api.patch(`/leads/${id}/stage`, { stage }),
    onSuccess: invalidate,
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div className="space-y-4">
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1.5 flex-wrap">
          {LEAD_TABS.map((t) => (
            <button
              key={t.key} data-testid={LEADS.tab(t.key)} onClick={() => setActiveTab(t.key)}
              className="text-sm font-medium rounded-[10px] px-3.5 py-1.5 transition-colors"
              style={activeTab === t.key ? { background: "#D6202B", color: "#fff" } : { background: "transparent", border: "1px solid #EBEAE5", color: "#68686E" }}
            >
              {t.label} <span className="ml-1 opacity-70">{t.key === "converted" ? convertedToday.length : (bucketed[t.key] || []).length}</span>
              {t.key === "contacted" && overdueContactedCount > 0 && (
                <span
                  data-testid={LEADS.contactedOverdueCount}
                  className="ml-1.5 inline-flex items-center justify-center text-[11px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px]"
                  style={{ background: "var(--status-overdue-dot, #F04438)", color: "#fff" }}
                >
                  {overdueContactedCount}
                </span>
              )}
            </button>
          ))}
        </div>
        <Button data-testid={LEADS.captureButton} onClick={() => setCaptureOpen(true)} className="text-white gap-2" style={{ background: "#D6202B" }}>
          <UserPlus size={15} /> Capture lead
        </Button>
      </motion.div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative w-56">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#68686E" }} />
          <Input data-testid={LEADS.filterSearchInput} placeholder="Search name or phone…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger data-testid={LEADS.filterSourceSelect} className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {sourceOptions.map((s) => <SelectItem key={s} value={s}>{sourceLabel(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        {branchId === "all" && (
          <Select value="all" onValueChange={() => {}}>
            <SelectTrigger data-testid={LEADS.filterBranchSelect} className="w-40" disabled><SelectValue placeholder="All branches" /></SelectTrigger>
            <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {activeTab === "contacted" && (
          <Select value={subStatusFilter} onValueChange={setSubStatusFilter}>
            <SelectTrigger data-testid={LEADS.filterSubStatusSelect} className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sub-statuses</SelectItem>
              {CONTACTED_SUB_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <SortControl fields={LEAD_SORT_FIELDS} sortBy={sortBy} sortDir={sortDir} onChange={setSort} testId={LEADS.sortControl} />
      </div>

      {(activeTab === "converted" ? convertedQ.isError : leadsQ.isError) && (
        <ErrorState error={activeTab === "converted" ? convertedQ.error : leadsQ.error} onRetry={() => (activeTab === "converted" ? convertedQ.refetch() : leadsQ.refetch())} />
      )}
      {(activeTab === "converted" ? convertedQ.isLoading : leadsQ.isLoading) && <GymLoader label="Loading leads…" />}

      {!(activeTab === "converted" ? convertedQ.isLoading || convertedQ.isError : leadsQ.isLoading || leadsQ.isError) && (
        <LeadTable
          tabKey={activeTab} leads={filteredLeads} planById={planById}
          onRowClick={setDetailLead}
          onSubStatusChange={(id, stage) => stageMutation.mutate({ id, stage })}
          onMarkContacted={(id) => stageMutation.mutate({ id, stage: "contacted" })}
          onConvert={setConvertLead}
          onMarkLost={(id) => stageMutation.mutate({ id, stage: "lost" })}
          onReopen={(id) => stageMutation.mutate({ id, stage: "new_lead" })}
          onViewMember={(lead) => navigate(memberDetailPath(lead.converted_member_ref))}
        />
      )}

      <LeadDetailDrawer
        lead={detailLead} planById={planById} branchNameById={branchNameById}
        onOpenChange={(v) => !v && setDetailLead(null)}
        onConvert={() => { setConvertLead(detailLead); setDetailLead(null); }}
        onMarkLost={() => { stageMutation.mutate({ id: detailLead.id, stage: "lost" }); setDetailLead(null); }}
      />
      <ConvertLeadWizard
        lead={convertLead} branchId={branchId} open={!!convertLead}
        onOpenChange={(v) => { if (!v) { setConvertLead(null); invalidate(); } }}
      />
      <LeadCaptureForm open={captureOpen} onOpenChange={setCaptureOpen} branchId={branchId} branches={branches} />
    </div>
  );
}
